import * as styles from "./Header.css";
import { detailCard } from "./fields.css";

import { useAtomValue, useSetAtom } from "jotai";
import { Factory, UserRound } from "lucide-react";
import { useEffect, useState } from "react";

import { Avatar } from "@frontend/components/atoms/Avatar";
import { Checkbox } from "@frontend/components/atoms/Checkbox";
import {
  Field,
  FieldContent,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { Fieldset } from "@frontend/components/atoms/Fieldset";
import { Input } from "@frontend/components/atoms/Input";
import { ImageEditor } from "@frontend/components/organisms/ImageEditor";
import { useField } from "@frontend/hooks/form";
import { Trans } from "@lingui/react/macro";

import { useContactForm } from "../../hooks/useContactForm";
import { isEditModeAtom, pendingPhotoAtom } from "../../stores";
import { selectedContactAtom } from "../../stores";

export function Header() {
  const { contactForm } = useContactForm();

  const editMode = useAtomValue(isEditModeAtom);
  const selectedContact = useAtomValue(selectedContactAtom);

  const firstName = useField(contactForm.fields.firstName);
  const lastName = useField(contactForm.fields.lastName);
  const organization = useField(contactForm.fields.organization);
  const organizationSubName = useField(contactForm.fields.organizationSubName);
  const isOrganization = useField(contactForm.fields.isOrganization);

  const setPendingPhoto = useSetAtom(pendingPhotoAtom);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [prevEditMode, setPrevEditMode] = useState(editMode);

  const avatarUrl = selectedContact?.hasPhoto
    ? selectedContact.avatarUrl || ""
    : undefined;

  const photoUrl = previewUrl ?? avatarUrl;

  const fallback = isOrganization.value ? <Factory /> : <UserRound />;

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  if (editMode !== prevEditMode) {
    setPrevEditMode(editMode);

    if (!editMode) {
      setPreviewUrl(null);
      setPendingPhoto(null);
    }
  }

  const handleFileSelect = (file: File) => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreviewUrl(URL.createObjectURL(file));
    setPendingPhoto(file);
  };

  return (
    <>
      {editMode ? (
        <>
          <ImageEditor aspect={1} onConfirm={handleFileSelect}>
            <Avatar
              className={styles.avatar({ editMode })}
              url={photoUrl}
              size="lg"
              fallback={fallback}
              editMode={true}
            />
          </ImageEditor>

          <Fieldset
            variant="card"
            orientation="horizontal"
            className={detailCard}
          >
            <Field>
              <FieldLabel>
                <Trans>Organization</Trans>
              </FieldLabel>
              <FieldContent>
                <Checkbox
                  checked={isOrganization.value}
                  onCheckedChange={(checked) =>
                    isOrganization.setValue(checked)
                  }
                />
              </FieldContent>
            </Field>

            {true === isOrganization.value ? (
              <>
                <Field>
                  <FieldLabel>
                    <Trans>Organization Name</Trans>
                  </FieldLabel>
                  <FieldContent>
                    <Input
                      size="sm"
                      invalid={null !== organization.error}
                      value={organization.value}
                      onValueChange={organization.setValue}
                    />
                  </FieldContent>
                </Field>
                <Field>
                  <FieldLabel>
                    <Trans>Name complement</Trans>
                  </FieldLabel>
                  <FieldContent>
                    <Input
                      size="sm"
                      value={organizationSubName.value}
                      onValueChange={organizationSubName.setValue}
                    />
                  </FieldContent>
                </Field>
              </>
            ) : (
              <>
                <Field>
                  <FieldLabel>
                    <Trans>First Name</Trans>
                  </FieldLabel>
                  <FieldContent>
                    <Input
                      size="sm"
                      invalid={null !== firstName.error}
                      value={firstName.value}
                      onValueChange={firstName.setValue}
                    />
                  </FieldContent>
                </Field>
                <Field>
                  <FieldLabel>
                    <Trans>Last Name</Trans>
                  </FieldLabel>
                  <FieldContent>
                    <Input
                      size="sm"
                      invalid={null !== lastName.error}
                      value={lastName.value}
                      onValueChange={lastName.setValue}
                    />
                  </FieldContent>
                </Field>
              </>
            )}
          </Fieldset>
        </>
      ) : (
        <header className={styles.header}>
          <Avatar
            className={styles.avatar({ editMode })}
            url={photoUrl}
            fallback={fallback}
            size="lg"
          />

          <div className={styles.displayName}>
            {selectedContact?.displayName}

            {selectedContact?.isOrganization &&
            selectedContact.organizationSubName ? (
              <div className={styles.subName}>
                {selectedContact.organizationSubName}
              </div>
            ) : null}
          </div>
        </header>
      )}
    </>
  );
}
